import { useContext } from 'react';
import PropTypes from 'prop-types';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowsAltV,
  faCancel,
  faEdit,
  faSpinner,
  faTrashAlt,
} from '@fortawesome/free-solid-svg-icons';
import { Button } from 'react-bootstrap';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import BlockListShape from './BlockListShape';
import { BlockListContext } from './BlockListContext';

const propTypes = {
  value: BlockListShape,
  deleteBlockId: PropTypes.string,
  reOrderBlocks: PropTypes.bool,
};
const defaultProps = {
  value: {},
  deleteBlockId: '',
  reOrderBlocks: false,
};

export default function BLockListItem({ value, deleteBlockId, reOrderBlocks }) {
  const { onClickEdit, onClickDelete } = useContext(BlockListContext);
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({
      id: value.id,
      transition: {
        duration: 150, // milliseconds
        easing: 'cubic-bezier(0.25, 1, 0.5, 1)',
      },
    });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const blockTypeText = (type = '') => {
    const label = {
      html: 'HTML',
      files: 'Files',
      item_list: 'Item List',
    };

    return label[type.toLowerCase()];
  };

  const handleClickEdit = (e) => {
    onClickEdit(e, value);
  };

  const handleClickDelete = (e) => onClickDelete(e, value.id);

  const renderActions = () => {
    let result;

    if (deleteBlockId !== value.id) {
      result = (
        <>
          {reOrderBlocks ? (
            <span className="d-block btn btn-link text-muted">
              <FontAwesomeIcon
                icon={faSpinner}
                spin
                pulse
                className="text-danger"
              />
            </span>
          ) : (
            <span
              className="d-block btn btn-link text-muted"
              {...listeners}
              {...attributes}
            >
              <FontAwesomeIcon icon={faArrowsAltV} />
            </span>
          )}
          <Button
            variant="link"
            type="button"
            className="btn btn-link text-muted"
            onClick={handleClickEdit}
            data-testid="Button-edit"
          >
            <FontAwesomeIcon icon={faEdit} />
          </Button>
          <Button
            variant="link"
            type="button"
            className="btn btn-link text-muted"
            onClick={handleClickDelete}
            data-testid="Button-delete"
          >
            <FontAwesomeIcon icon={faTrashAlt} />
          </Button>
        </>
      );
    } else {
      result = (
        <>
          <span className="d-block btn btn-link text-muted">
            <FontAwesomeIcon icon={faCancel} />
          </span>
          <span className="d-block btn btn-link text-muted">
            <FontAwesomeIcon icon={faCancel} />
          </span>
          <span className="d-block btn btn-link text-muted">
            <FontAwesomeIcon
              icon={faSpinner}
              spin
              pulse
              className="text-danger"
            />
          </span>
        </>
      );
    }

    return result;
  };

  return (
    <div
      className="content-block mb-2 "
      id={`block-${value.id}`}
      ref={setNodeRef}
      style={style}
      data-testid="Wrapper-listItem"
    >
      <div className="content-block-details d-flex align-items-center align-content-center justify-content-between">
        <div className="action-buttons pe-2">
          <div
            className="btn-group btn-group-sm"
            role="group"
            aria-label="Basic example"
          >
            {renderActions()}
          </div>
        </div>
        <div className="block-title flex-grow-1 px-2 d-flex align-items-center align-content-center">
          <strong data-testid="Text-title">
            {value.title.length > 0 ? value.title : '(no title)'}
          </strong>
        </div>
        <div className="block-type px-2 d-flex align-items-center align-content-center">
          {value.contentType && (
            <span
              className="badge bg-info text-capitalize"
              data-testid="Badge-type"
            >
              {blockTypeText(value.contentType)}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

BLockListItem.propTypes = propTypes;
BLockListItem.defaultProps = defaultProps;
